<ng-container *transloco="let t; prefix: 'orders.customerConfiguration'">
  @if (customer(); as company) {
    <mat-card class="customer-summary" appearance="outlined">
      <mat-card-content class="customer-summary-content">
        <span class="customer-summary-name headline-small-emphasized">{{ company.ragioneSociale }}</span>
        <div class="customer-summary-field">
          <span class="body-large-emphasized">{{ t('taxCode') }}</span>
          <span class="body-large">{{ company.codiceFiscale || '—' }}</span>
        </div>
        <div class="customer-summary-field">
          <span class="body-large-emphasized">{{ t('vatNumber') }}</span>
          <span class="body-large">{{ company.partitaIva || '—' }}</span>
        </div>
      </mat-card-content>
    </mat-card>

    <div class="destinations-heading">
      <span class="title-medium-emphasized">{{ t('destinationsTitle') }}</span>
      @if (!creatingDestination()) {
        <button matButton="outlined" type="button" (click)="openNewDestination()">{{ t('newDestination') }}</button>
      } @else {
        <button matButton type="button" (click)="backToDestinations()">{{ t('backToDestinations') }}</button>
      }
    </div>

    @if (creatingDestination()) {
      <form class="new-destination-form" [formGroup]="newDestinationForm" (ngSubmit)="continue()">
        <div class="new-destination-fields">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('reference') }}</mat-label>
            <input matInput formControlName="riferimento" required>
            <mat-error>{{ t('requiredField') }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('city') }}</mat-label>
            <input matInput formControlName="citta" autocomplete="address-level2" required>
            <mat-error>{{ t('requiredField') }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('province') }}</mat-label>
            <input matInput formControlName="provincia" maxlength="2" autocomplete="address-level1" required>
            <mat-error>{{ t('provinceError') }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('postcode') }}</mat-label>
            <input matInput formControlName="cap" inputmode="numeric" maxlength="5" autocomplete="postal-code" required>
            <mat-error>{{ t('postcodeError') }}</mat-error>
          </mat-form-field>
          <mat-form-field class="new-destination-address" appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('address') }}</mat-label>
            <input matInput formControlName="indirizzo" autocomplete="street-address" required>
            <mat-error>{{ t('requiredField') }}</mat-error>
          </mat-form-field>
        </div>

        <span class="destination-notes-title title-medium-emphasized">{{ t('deliveryNoteDetails') }}</span>
        <div class="new-destination-fields">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('days') }}</mat-label>
            <input matInput formControlName="giorni">
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('hours') }}</mat-label>
            <input matInput formControlName="orari">
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('contact') }}</mat-label>
            <input matInput formControlName="contatto">
          </mat-form-field>
          <mat-form-field class="new-destination-note text-area-custom" appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ t('freeNote') }}</mat-label>
            <textarea matInput formControlName="notaLibera" rows="4"></textarea>
          </mat-form-field>
        </div>
        <div class="destination-note-toggles">
          <mat-slide-toggle formControlName="inBolla" labelPosition="before">{{ t('includeInDeliveryNote') }}</mat-slide-toggle>
          <mat-slide-toggle formControlName="sponda" labelPosition="before">{{ t('tailLift') }}</mat-slide-toggle>
        </div>
        <div class="configuration-actions">
          <button matButton="filled" type="submit" [disabled]="newDestinationForm.invalid">{{ t('continue') }}</button>
        </div>
      </form>
    } @else {
    <mat-form-field class="destination-search" appearance="outline" subscriptSizing="dynamic">
      <input matInput [formControl]="searchControl" [placeholder]="t('searchDestinations')" [attr.aria-label]="t('searchDestinations')">
      <i matSuffix class="fa-regular fa-magnifying-glass" aria-hidden="true"></i>
    </mat-form-field>

    @if (loading()) {
      <div class="destinations-state" role="status" [attr.aria-label]="t('loading')"><ui-loading-spinner /></div>
    } @else if (loadFailed()) {
      <div class="destinations-state">
        <span class="body-medium" role="alert">{{ t('loadError') }}</span>
        <button matButton type="button" (click)="loadDestinations()">{{ t('retry') }}</button>
      </div>
    } @else {
      <mat-radio-group class="destination-list" [attr.aria-label]="t('destinationsTitle')" [value]="configuration.selectedDestinationId()" (change)="selectDestination($event.value)">
        @for (destination of configuration.destinations(); track destination.id) {
          <div class="destination-card" [class.destination-card--selected]="configuration.selectedDestinationId() === destination.id" (click)="selectDestination(destination.id)">
            <div class="destination-card-heading">
              <mat-radio-button [value]="destination.id" [attr.aria-label]="destination.riferimento || destination.indirizzo">
                <span class="destination-type body-medium-emphasized">{{ destination.riferimento || t('destination') }}</span>
              </mat-radio-button>
              <a matIconButton [href]="mapUrl(destination)" target="_blank" rel="noopener noreferrer" [attr.aria-label]="t('openMap', { address: destination.indirizzo })" (click)="$event.stopPropagation()">
                <i class="fa-regular fa-location-dot" aria-hidden="true"></i>
              </a>
            </div>
            <div class="destination-address">
              <span class="body-large-emphasized">{{ destination.indirizzo || '—' }}</span>
              <span class="body-large">{{ destination.cap }} {{ destination.citta }}@if (destination.provincia) { ({{ destination.provincia }}) }</span>
            </div>
            @if (configuration.selectedDestinationId() === destination.id) {
              <form class="existing-destination-form" [formGroup]="existingDestinationForm" (click)="$event.stopPropagation()" (ngSubmit)="continue()">
                <span class="title-medium-emphasized">{{ t('deliveryNoteDetails') }}</span>
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t('reference') }}</mat-label>
                  <input matInput formControlName="riferimento">
                </mat-form-field>
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t('days') }}</mat-label>
                  <input matInput formControlName="giorni">
                </mat-form-field>
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t('hours') }}</mat-label>
                  <input matInput formControlName="orari">
                </mat-form-field>
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t('contact') }}</mat-label>
                  <input matInput formControlName="contatto">
                </mat-form-field>
                <mat-form-field class="text-area-custom" appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ t('freeNote') }}</mat-label>
                  <textarea matInput formControlName="notaLibera" rows="4" [maxlength]="noteMaxLength" [placeholder]="t('notePlaceholder')"></textarea>
                  <mat-hint align="end">{{ t('noteCharacterCount', { count: existingDestinationForm.controls.notaLibera.value.length, max: noteMaxLength }) }}</mat-hint>
                  <mat-error>{{ t('noteTooLong', { max: noteMaxLength }) }}</mat-error>
                </mat-form-field>
                <div class="destination-note-toggles">
                  <mat-slide-toggle formControlName="inBolla" labelPosition="before">{{ t('includeInDeliveryNote') }}</mat-slide-toggle>
                  <mat-slide-toggle formControlName="sponda" labelPosition="before">{{ t('tailLift') }}</mat-slide-toggle>
                </div>
              </form>
            }
          </div>
        } @empty {
          <div class="destinations-state" role="status">
            <span class="body-large">{{ t('noDestinations') }}</span>
          </div>
        }
      </mat-radio-group>
    }

    <div class="configuration-actions">
      <button matButton="filled" type="button" [disabled]="!configuration.selectedDestination() || loading() || loadFailed() || existingDestinationForm.invalid" (click)="continue()">{{ t('continue') }}</button>
    </div>
    }
  } @else {
    <div class="destinations-state">
      <span class="body-large">{{ t('missingCustomer') }}</span>
      <a matButton="outlined" [routerLink]="['/new-order-proposal/new-customer']" [queryParams]="{ id: proposalId }">{{ t('backToSearch') }}</a>
    </div>
  }
</ng-container>
